/**
 * Dual-filter (Kawase) bloom.
 *
 * Down with a 13-tap, up with a 9-tap tent, accumulating additively on the way
 * back. Six levels from a 640x360 buffer puts the widest sample footprint at
 * roughly a third of the screen, which is the whole point: the skirt has to
 * carry colour outward far enough to read as neon rather than as a halo.
 */

import { GLSL_COLOR, GLSL_TAP13 } from './common';

/**
 * Bright pass fused with the first downsample. Prefiltering per tap rather than
 * once on the averaged result keeps a single overbright pixel from being
 * averaged below the threshold and vanishing.
 */
export const BLOOM_BRIGHT_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D tSource;
uniform vec2 uTexel;
uniform float uThreshold;
uniform float uKnee;

in vec2 vUv;
out vec4 fragColor;

${GLSL_COLOR}

// Soft-knee curve: quadratic ramp across [threshold - knee, threshold + knee],
// linear above. A hard cut makes the bloom pop on and off as things move.
vec3 prefilter(vec3 c) {
  float br = max(c.r, max(c.g, c.b));
  float rq = clamp(br - uThreshold + uKnee, 0.0, 2.0 * uKnee);
  rq = (rq * rq) / (4.0 * uKnee + 1e-4);
  return c * (max(rq, br - uThreshold) / max(br, 1e-4));
}

#define FETCH(uv) prefilter(max(texture(tSource, uv).rgb, vec3(0.0)))

${GLSL_TAP13}

void main() {
  fragColor = vec4(tap13(vUv, uTexel), 1.0);
}
`;

/** Plain 13-tap halving step for every level after the first. */
export const BLOOM_DOWN_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D tSource;
uniform vec2 uTexel;

in vec2 vUv;
out vec4 fragColor;

#define FETCH(uv) texture(tSource, uv).rgb

${GLSL_TAP13}

void main() {
  fragColor = vec4(tap13(vUv, uTexel), 1.0);
}
`;

/**
 * Nine-tap tent upsample. Drawn with additive blending straight into the level
 * above, so the accumulation costs nothing extra. `uWeight` is the per-level
 * term from `BLOOM.weights`; `uRadius` widens the tent in source texels.
 *
 * Alpha is written as zero: nothing downstream reads the bloom buffer's alpha,
 * and letting it accumulate past one only invites a driver-dependent surprise.
 */
export const BLOOM_UP_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D tSource;
uniform vec2 uTexel;
uniform float uRadius;
uniform float uWeight;

in vec2 vUv;
out vec4 fragColor;

void main() {
  vec2 d = uTexel * uRadius;

  vec3 a = texture(tSource, vUv + vec2(-d.x,  d.y)).rgb;
  vec3 b = texture(tSource, vUv + vec2( 0.0,  d.y)).rgb;
  vec3 c = texture(tSource, vUv + vec2( d.x,  d.y)).rgb;

  vec3 e = texture(tSource, vUv + vec2(-d.x,  0.0)).rgb;
  vec3 f = texture(tSource, vUv).rgb;
  vec3 g = texture(tSource, vUv + vec2( d.x,  0.0)).rgb;

  vec3 h = texture(tSource, vUv + vec2(-d.x, -d.y)).rgb;
  vec3 i = texture(tSource, vUv + vec2( 0.0, -d.y)).rgb;
  vec3 j = texture(tSource, vUv + vec2( d.x, -d.y)).rgb;

  vec3 sum = (f * 4.0 + (b + e + g + i) * 2.0 + (a + c + h + j)) * 0.0625;
  fragColor = vec4(sum * uWeight, 0.0);
}
`;
